import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LogOut, Plus, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function OffboardingPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id:"", employee_name:"", reason:"", last_day:"" });
  const [employees, setEmployees] = useState([]);

  const load = () => api.get("/offboarding").then(r=>setItems(r.data)).catch(()=>setItems([]));
  useEffect(() => {
    load();
    api.get("/employees").then(r=>setEmployees(r.data)).catch(()=>setEmployees([]));
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setOpen(true);
      const p = new URLSearchParams(searchParams); p.delete("action"); setSearchParams(p, { replace: true });
    }
  }, [searchParams]);

  const start = async () => {
    if (!form.employee_id || !form.last_day) return toast.error("Employee and last day required");
    await api.post("/offboarding/start", form);
    toast.success("Offboarding started"); setOpen(false); load();
  };

  const completeTask = async (off_id, task_id) => {
    await api.patch(`/offboarding/${off_id}/task/${task_id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="offboarding-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display flex items-center gap-2">
            <LogOut className="w-6 h-6 text-pink" /> Offboarding
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Track exits with a repeatable checklist so nothing slips.</p>
        </div>
        <Button className="btn-primary rounded-full" onClick={()=>setOpen(true)} data-testid="offb-new-btn"><Plus className="w-4 h-4 mr-1" /> Start offboarding</Button>
      </div>
      <div className="space-y-4">
        {items.length === 0 && <div className="text-sm text-neutral-500">No offboardings in progress.</div>}
        {items.map(off => (
          <Card key={off.off_id} className="border-0 shadow-sm" data-testid={`offb-${off.off_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{off.employee_name}</h3>
                    <Badge variant="outline" className="text-[9px]">{off.status}</Badge>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">Last day: {off.last_day} · Started {new Date(off.started_at).toLocaleDateString()}</div>
                  {off.reason && <div className="text-xs text-neutral-600 mt-1 italic">"{off.reason}"</div>}
                </div>
                <div className="text-xs font-mono text-neutral-500">
                  {off.tasks.filter(t=>t.status==="COMPLETED").length} / {off.tasks.length} tasks
                </div>
              </div>
              <div className="mt-4 space-y-1.5">
                {off.tasks.map(t => (
                  <div key={t.task_id} className="flex items-center gap-2 text-sm py-1.5">
                    <button
                      onClick={()=>t.status!=="COMPLETED" && completeTask(off.off_id, t.task_id)}
                      data-testid={`offb-task-${off.off_id}-${t.task_id}`}
                      disabled={t.status==="COMPLETED"}
                      className="disabled:opacity-100">
                      {t.status==="COMPLETED" ? <CheckCircle2 className="w-4 h-4 text-teal" /> : <Circle className="w-4 h-4 text-neutral-300" />}
                    </button>
                    <span className={t.status==="COMPLETED" ? "line-through text-neutral-400" : "text-neutral-700"}>{t.title}</span>
                    <Badge variant="outline" className="text-[9px] ml-auto">{t.type}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="offb-new-dialog">
          <DialogHeader><DialogTitle>Start offboarding</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Employee</Label>
              <select className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200" value={form.employee_id} onChange={e=>{
                const emp = employees.find(x=>x.employee_id===e.target.value);
                setForm({...form, employee_id: e.target.value, employee_name: emp?.name || ""});
              }} data-testid="offb-employee-select">
                <option value="">Choose employee…</option>
                {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name} — {e.department}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Last day</Label><Input type="date" value={form.last_day} onChange={e=>setForm({...form, last_day: e.target.value})} data-testid="offb-lastday" /></div>
            <div><Label className="text-xs">Reason (optional)</Label><Textarea rows={2} value={form.reason} onChange={e=>setForm({...form, reason: e.target.value})} placeholder="Resigned, made redundant, mutual etc." /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={start} data-testid="offb-start-btn">Start</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
